import React from 'react';
import { Tour } from 'antd';
import { createGlobalStyle } from 'styled-components';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
/** Button styles for Scope tour (dark-orange primary, teal secondary). */
export const SCOPE_TOUR_NEXT_BUTTON_STYLE: React.CSSProperties = {
  backgroundColor: 'var(--btn-accent-bg, #ffbc3e)',
  color: 'var(--color-on-primary, #052325)',
  borderColor: 'var(--btn-accent-bg, #ffbc3e)',
  borderRadius: 50,
  fontWeight: 600,
  fontSize: 12,
  lineHeight: '16px',
  padding: '6px 16px',
  minHeight: 32,
};

export const SCOPE_TOUR_PREV_BUTTON_STYLE: React.CSSProperties = {
  color: 'var(--color-primary, #41aaaf)',
  borderColor: 'var(--color-primary, #41aaaf)',
  borderRadius: 50,
  fontWeight: 600,
  fontSize: 12,
  lineHeight: '16px',
  padding: '6px 16px',
  minHeight: 32,
};

/**
 * Class for every tour step heading (use on the element that wraps the title string).
 * Styled in {@link ScopeTourStyles}.
 */
export const SCOPE_TOUR_STEP_TITLE_CLASS = 'scope-tour-step-title';

/** Global styles for Scope tours – padding, rounded corners, step content. */
export const ScopeTourStyles = createGlobalStyle`
  .scope-tour .ant-tour-inner {
    padding: 24px !important;
    border-radius: 16px !important;
    background: var(--color-surface-raised, #ffffff) !important;
    color: var(--color-text-base, #2e2e2e) !important;
    border: 1px solid var(--color-border, #dbeae3) !important;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
  }

  .scope-tour .ant-tour-content {
    border-radius: 16px !important;
    overflow: hidden;
  }

  .scope-tour .ant-tour-close {
    color: var(--color-text-muted, #5a6a6a) !important;
  }

  .scope-tour .ant-tour-close:hover {
    color: var(--color-text-base, #2e2e2e) !important;
  }

  .scope-tour .ant-tour-description,
  .scope-tour .scope-tour-step-description {
    padding-bottom: 24px;
    padding-top: 4px;
    color: var(--color-text-base, #2e2e2e) !important;
  }

  .scope-tour .ant-tour-indicators .ant-tour-indicator {
    background: var(--color-border, #dbeae3) !important;
  }

  .scope-tour .ant-tour-indicators .ant-tour-indicator-active {
    background: var(--color-primary, #41aaaf) !important;
  }

  .scope-tour .scope-tour-step-cover {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    width: 100%;
  }

  .scope-tour .scope-tour-step-cover-img {
    width: 250px !important;
    max-width: 250px !important;
    height: auto !important;
    display: block;
  }

  .scope-tour .${SCOPE_TOUR_STEP_TITLE_CLASS} {
    font-weight: 700 !important;
    font-size: 16px !important;
    line-height: 1.35 !important;
    letter-spacing: -0.02em;
    color: var(--color-text-base, #2e2e2e) !important;
    text-align: left;
    width: 100%;
    margin: 0;
    padding: 18px 0 8px 0;
    box-sizing: border-box;
  }

  .scope-tour .scope-tour-step-cover-img + .${SCOPE_TOUR_STEP_TITLE_CLASS} {
    padding-top: 4px;
    margin-top: 0;
  }

  html[data-theme='dark'] .scope-tour .ant-tour-inner {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45) !important;
  }

  /* PWA phone: bottom-sheet style tour card (all ScopeTour instances) */
  .scope-tour--pwa.ant-tour,
  .scope-tour--pwa .ant-tour {
    position: fixed !important;
    left: 12px !important;
    right: 12px !important;
    top: auto !important;
    bottom: calc(var(--pwa-bottom-nav-height, 64px) + 12px + env(safe-area-inset-bottom, 0px)) !important;
    width: auto !important;
    max-width: none !important;
    max-height: min(72dvh, calc(100dvh - var(--pwa-bottom-nav-height, 64px) - 48px)) !important;
    transform: none !important;
    inset-inline-start: 12px !important;
    inset-inline-end: 12px !important;
  }

  .scope-tour--pwa .ant-tour-arrow {
    display: none !important;
  }

  .scope-tour--pwa .ant-tour-content {
    width: 100% !important;
    max-height: inherit;
  }

  .scope-tour--pwa .ant-tour-inner {
    padding: 16px 16px 14px !important;
    border-radius: 20px !important;
    max-height: inherit;
    overflow: auto !important;
    -webkit-overflow-scrolling: touch;
    box-sizing: border-box !important;
  }

  .scope-tour--pwa .ant-tour-description,
  .scope-tour--pwa .scope-tour-step-description {
    padding-bottom: 12px;
    font-size: 13px;
    line-height: 1.45;
  }

  .scope-tour--pwa .scope-tour-step-cover {
    gap: 10px;
  }

  .scope-tour--pwa .scope-tour-step-cover-img {
    width: min(180px, 52vw) !important;
    max-width: min(180px, 52vw) !important;
  }

  .scope-tour--pwa .${SCOPE_TOUR_STEP_TITLE_CLASS} {
    font-size: 15px !important;
    padding: 8px 0 4px 0;
  }

  .scope-tour--pwa .ant-tour-footer {
    margin-top: 4px;
  }

  .scope-tour--pwa .ant-tour-buttons .ant-btn {
    min-height: 36px;
    padding-inline: 14px;
  }

  .scope-tour--pwa .ant-tour-close {
    inset-inline-end: 10px !important;
    top: 10px !important;
  }
`;

export interface ScopeTourStep {
  target?: HTMLElement | (() => HTMLElement) | null;
  title?: React.ReactNode;
  description: React.ReactNode;
  cover?: React.ReactNode;
  placement?: 'left' | 'right' | 'top' | 'bottom' | 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | 'leftTop' | 'leftBottom' | 'rightTop' | 'rightBottom';
}

export interface ScopeTourProps {
  open: boolean;
  onClose: () => void;
  steps: ScopeTourStep[];
  /** Optional custom class for the tour root (default: scope-tour). */
  className?: string;
}

/**
 * Reusable tour component with Scope styling. Use for product tours across pages.
 * Steps use description (and optional title/cover); targets and placement per step.
 * On PWA phone nav, the panel becomes a bottom sheet so every tour fits the screen.
 */
export const ScopeTour: React.FC<ScopeTourProps> = ({
  open,
  onClose,
  steps,
  className = 'scope-tour',
}) => {
  const pwaMobileNav = usePwaMobileNavEnabled();

  const antSteps = React.useMemo(
    () =>
      steps.map((step) => ({
        title: step.title ?? null,
        description: step.description,
        cover: step.cover,
        // On PWA, keep a body target so Ant still mounts the panel, but CSS
        // pins it as a bottom sheet (arrow hidden) — avoids off-screen side placements.
        placement: pwaMobileNav ? ('bottom' as const) : step.placement ?? 'left',
        target: pwaMobileNav
          ? () => document.body
          : step.target,
        nextButtonProps: { style: SCOPE_TOUR_NEXT_BUTTON_STYLE },
        prevButtonProps: { style: SCOPE_TOUR_PREV_BUTTON_STYLE },
      })),
    [steps, pwaMobileNav]
  );

  const rootClassName = pwaMobileNav
    ? `${className} scope-tour--pwa`.trim()
    : className;

  return (
    <>
      <ScopeTourStyles />
      <Tour
        open={open}
        onClose={onClose}
        steps={antSteps}
        rootClassName={rootClassName}
        gap={pwaMobileNav ? { offset: 0, radius: 8 } : undefined}
      />
    </>
  );
};
